// =============================================================================
// Shared Types — used by all Lambda functions
//
// ⚠️  VENDORED COPY EXISTS — sync required on change
// A copy of this file lives at Darkfeld-Plugin/src/lib/types.ts.
// After editing this file, run from Darkfeld-Web/:
//
//   cp backend/shared/types/index.ts ../Darkfeld-Plugin/src/lib/types.ts
//   # Then restore the VENDORED COPY header at the top of the copied file.
//
// =============================================================================

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
export type UserRole = 'user' | 'dm' | 'moderator' | 'admin';
export type UserStatus = 'active' | 'banned' | 'suspended';

export interface User {
  userId: string;
  /** Discord handle — set at signup, immutable from the user's perspective. */
  username: string;
  /** User-chosen display name shown in the UI. Falls back to username when absent. */
  displayName?: string;
  discordId: string | null;
  email?: string;
  avatarUrl?: string;
  role: UserRole;
  status: UserStatus;
  joinedAt: string;
  lastSeen: string;
  /**
   * S3 object key for a user-uploaded custom avatar (e.g. `avatars/{userId}`).
   * When present, the API generates a pre-signed GET URL and returns it as
   * `avatarUrl` in the response, overriding the Discord CDN URL.
   * When absent, `avatarUrl` is the Discord CDN URL set at signup.
   */
  avatarKey?: string;
  /**
   * True for accounts imported from the legacy phpBB site.
   * These users have no Discord login and cannot authenticate.
   */
  isLegacy?: boolean;
  /** Original phpBB user_id — present on legacy accounts only. */
  legacyPhpbbId?: number;
  /** Post count from the legacy phpBB forum — present on legacy accounts only. */
  legacyPostCount?: number;
  /**
   * Manually granted by admins to recognise supporters or contributors.
   * When true the UI shows a "Gilded" rose-gold badge (Crown icon + shimmer name
   * + avatar ring) wherever the user's identity is displayed.
   */
  isSupporter?: boolean;
}

// ---------------------------------------------------------------------------
// Message Board
// ---------------------------------------------------------------------------
export interface Board {
  boardId: string;
  name: string;
  description: string;
  icon?: string;
  requiredRole?: UserRole;
  postCount: number;
  sortOrder: number;
  createdAt: string;
  /**
   * True for boards imported from the legacy phpBB forum.
   * Derived at read time from `boardId.startsWith('phpbb-forum-')` — not stored in DynamoDB.
   * Legacy boards are read-only; new posts are rejected by the API with 403.
   */
  isLegacy?: boolean;
  /**
   * True when the board is locked and does not accept new posts.
   * Currently coextensive with isLegacy but may be set independently in future.
   */
  isLocked?: boolean;
}

export interface Post {
  postId: string;
  boardId: string;
  authorId: string;
  /** Stored at creation time for display — may be stale if the user renames or changes avatar. */
  authorUsername?: string;
  authorDisplayName?: string;
  authorAvatarUrl?: string;
  title: string;
  content: string;        // JSON (Tiptap) or Markdown
  contentFormat: 'tiptap' | 'markdown';
  isPinned: boolean;
  isLocked: boolean;
  replyCount: number;
  reactionCounts: Record<string, number>;
  createdAt: string;
  updatedAt: string;
  /** Set when this post was auto-created by the Discord pin sync. */
  discordChannelId?: string;
}

export interface Reply {
  replyId: string;
  postId: string;
  authorId: string;
  /** Stored at creation time for display — may be stale if the user renames or changes avatar. */
  authorUsername?: string;
  authorDisplayName?: string;
  authorAvatarUrl?: string;
  content: string;
  contentFormat?: 'tiptap' | 'markdown';
  parentReplyId?: string;
  reactionCounts: Record<string, number>;
  createdAt: string;
  updatedAt: string;
  /** Discord message ID — set when this reply was created by the Discord pin sync. */
  discordMessageId?: string;
}

/** Sync state record stored per Discord channel. PK=DISCORD_SYNC, SK=CHANNEL#{channelId}. */
export interface DiscordPinSyncState {
  channelId: string;
  channelName: string;
  boardId: string;
  postId: string;
  /** Full DynamoDB PK of the board post — cached to avoid GSI lookup on every sync. */
  postPK: string;
  /** Full DynamoDB SK of the board post — cached to avoid GSI lookup on every sync. */
  postSK: string;
  lastSyncedAt: string;
  pinCount: number;
}

// ---------------------------------------------------------------------------
// D&D 3.5 / Pathfinder 1e Characters
// ---------------------------------------------------------------------------

/** Which ruleset this character uses */
export type RuleSystem = 'dnd35' | 'pathfinder1' | 'dnd5e' | 'custom';

export type DndAlignment =
  | 'LG' | 'NG' | 'CG'
  | 'LN' | 'TN' | 'CN'
  | 'LE' | 'NE' | 'CE';

export type SizeCategory =
  | 'Fine' | 'Diminutive' | 'Tiny' | 'Small' | 'Medium'
  | 'Large' | 'Huge' | 'Gargantuan' | 'Colossal';

export interface AbilityScores {
  str: number; dex: number; con: number;
  int: number; wis: number; cha: number;
}

// One entry per class when multiclassing
export interface CharacterClass {
  /** Class name (may reference a GameContent contentId for custom classes) */
  className: string;
  /** Subclass / archetype (PF1) or prestige class variant */
  subclass?: string;
  level: number;
  /** Hit die sides (e.g. 10 for d10) */
  hitDie: number;
  /** Whether this is the favored class for this character (PF1) */
  isFavoredClass?: boolean;
  /**
   * PF1 favored class bonus choice per level: 'hp' | 'skill' | custom string
   * Length must equal level.
   */
  favoredClassBonuses?: string[];
  // ---- Progression data (populated by Roll20 import or manual entry) ----
  /** Base Attack Bonus progression rate */
  babProgression?: 'full' | 'three_quarters' | 'half';
  /** Fortitude save progression */
  fortProgression?: 'good' | 'poor';
  /** Reflex save progression */
  refProgression?: 'good' | 'poor';
  /** Will save progression */
  willProgression?: 'good' | 'poor';
  /** Skill points per level (class base, not including INT modifier) */
  skillPointsPerLevel?: number;
}

export interface SpellEntry {
  spellName: string;
  /** References a GameContent contentId for custom spells */
  contentRef?: string;
  /** Caster class that grants this spell */
  castingClass: string;
  spellLevel: number;
  /** Slots prepared (prepared casters). Spontaneous casters leave undefined. */
  prepared?: number;
  /**
   * Which caster class this spell belongs to for multi-caster characters.
   * 1 = primary caster class, 2 = secondary caster class.
   * Omit for single-caster characters.
   */
  casterIndex?: 1 | 2;
  // ---- Full spell data (populated by Roll20 import or manual entry) ----
  /** e.g. "evocation", "conjuration (healing)" */
  school?: string;
  /** e.g. "1 standard action", "1 minute" */
  castingTime?: string;
  /** e.g. "V, S, M (a pinch of sand)" */
  components?: string;
  /** e.g. "close (25 ft. + 5 ft./2 levels)" */
  range?: string;
  /** Single-target description, e.g. "one creature" */
  target?: string;
  /** Area-effect description, e.g. "20-ft.-radius burst" */
  area?: string;
  /** e.g. "instantaneous", "1 round/level" */
  duration?: string;
  /** e.g. "Reflex half", "Will negates" */
  savingThrow?: string;
  /** e.g. "yes", "no", "yes (harmless)" */
  spellResistance?: string;
  /** Full spell description text */
  description?: string;
  /**
   * Supplemental notes — used for psionic power augmentation text,
   * roll templates, or other spell-specific DM/player notes.
   */
  notes?: string;
}

/**
 * A pool of spell points (or similar limited resource) tracked per character.
 * Covers PF1 psionics (Power Points), Harbinger Spell Points, Bardic Music uses, etc.
 */
export interface SpellPointPool {
  /** Display name, e.g. "Power Points", "Bardic Music", "Harbinger Spell Points" */
  name: string;
  /** Points remaining */
  current: number;
  /** Maximum points */
  max: number;
  /** Optional notes about the pool (e.g. breakdown of how max was derived) */
  notes?: string;
}

export interface SpellSlotTracker {
  /** Spell level (0–9) */
  [level: number]: { max: number; used: number };
}

/**
 * Per-caster spell slot tracker for multi-caster characters.
 * Maps caster index (1 or 2) → spell level → { max, used }.
 */
export type MultiCasterSlots = {
  [casterIndex: number]: SpellSlotTracker;
};

/**
 * Number of spells known per level for spontaneous casters (sorcerer, bard, oracle, etc.).
 * Maps spell level (0–9) to count of spells known at that level.
 * undefined = not tracked (prepared casters know all spells in their spellbook).
 */
export type SpellsKnownTracker = { [spellLevel: number]: number };

export interface EquipmentItem {
  name: string;
  quantity: number;
  /** Flavour / rules text for the item. */
  description?: string;
  /** Item category — mirrors CampaignMarketplaceItem for display consistency. */
  category?: ItemCategory;
  /** Item rarity — mirrors CampaignMarketplaceItem for display consistency. */
  rarity?: ItemRarity;
  weight?: number;
  /** Item cost in gp (populated by Roll20 import or manual entry) */
  cost?: number;
  /** Optional hyperlink to an external reference (e.g. Archives of Nethys, D&D Beyond). */
  sourceUrl?: string;
  /** References a GameContent contentId if this is a defined item */
  contentRef?: string;
  notes?: string;
  isEquipped?: boolean;
  /** Whether the item is currently carried (affects encumbrance) */
  isCarried?: boolean;
  slot?: string;  // e.g. "armor", "weapon_main", "ring_1"
}

export interface CharacterFeature {
  name: string;
  /** e.g. "Fighter 1", "Feat", "Race", "GameContent:feat_power_attack" */
  source: string;
  description: string;
  /** References a GameContent contentId for custom/imported features */
  contentRef?: string;
}

export interface SkillEntry {
  skillName: string;
  /** Ability score that governs this skill */
  ability: keyof AbilityScores;
  /** Total ranks invested */
  ranks: number;
  /**
   * Whether this is a class skill for at least one of the character's classes.
   * D&D 3.5: costs 2 skill points per rank if cross-class.
   * PF1: grants a flat +3 bonus when at least 1 rank is invested.
   */
  isClassSkill: boolean;
  /** Miscellaneous modifier (synergy, racial, item, etc.) */
  miscMod: number;
  /**
   * D&D 5e proficiency level for this skill.
   * 'none'      — not proficient (ability modifier only)
   * 'half'      — half proficiency (Jack of All Trades bard feature)
   * 'full'      — proficient (adds full Proficiency Bonus)
   * 'expertise' — double proficiency (Rogue/Bard expertise)
   * Absent on 3.5/PF1 records — treated as 'none' wherever read.
   */
  proficiency?: 'none' | 'half' | 'full' | 'expertise';
}

export type FlyManeuverability = 'clumsy' | 'poor' | 'average' | 'good' | 'perfect';

/**
 * Non-land movement speeds in feet.
 * The primary land speed is stored as `speed` on Character for backward compatibility.
 */
export interface AdditionalSpeeds {
  fly?: number;
  flyManeuverability?: FlyManeuverability;
  swim?: number;
  climb?: number;
  burrow?: number;
}

/**
 * Individual components that make up the total Armor Class.
 * All values are bonuses (+) or penalties (−).
 * Populated by Roll20 import; may be computed manually otherwise.
 */
export interface AcBreakdown {
  /** Armor bonus (from worn armor) */
  armor?: number;
  /** Shield bonus (from a held or worn shield) */
  shield?: number;
  /** DEX bonus actually applied (may be capped by armor max dex) */
  dex?: number;
  /** Natural armor bonus */
  natural?: number;
  /** Deflection bonus (e.g. from Ring of Protection) */
  deflection?: number;
  /** Dodge bonus (stackable) */
  dodge?: number;
  /** Miscellaneous bonuses not covered above */
  misc?: number;
  /** Size modifier — negative for Large+ creatures, positive for Small/smaller */
  size?: number;
}

/**
 * Metadata written once when a character is imported from D&D Beyond.
 * Not editable via PATCH /characters/{id}.
 */
export interface DnDBeyondImportMeta {
  /** D&D Beyond character ID (numeric, stored as string) */
  characterId: string;
  /** Full share URL, e.g. https://www.dndbeyond.com/characters/12345678 */
  characterUrl?: string;
  /** ISO-8601 timestamp of this import or most recent re-import */
  importedAt: string;
  /** Version of the dndbeyond.ts mapper used — for re-import compatibility */
  mapperVersion: string;
}

/**
 * Metadata written once when a character is imported from Roll20.
 * Not editable via PATCH /characters/{id}.
 */
export interface Roll20ImportMeta {
  /** Roll20 internal character ID */
  characterId: string;
  /** Name of the Roll20 campaign the export came from */
  campaignName?: string;
  /** Sheet template detected at import time */
  sheetTemplate: 'pathfinder_official' | 'pathfinder_npc' | 'dnd35_official' | 'unknown';
  /** ISO-8601 timestamp when the Roll20 campaign export was created (if known) */
  exportedAt?: string;
  /** ISO-8601 timestamp when this character was imported into Darkfeld */
  importedAt: string;
}

export interface Character {
  characterId: string;
  userId: string;
  name: string;
  isPublic: boolean;
  ruleSystem: RuleSystem;

  // Core identity
  race: string;
  /** References a GameContent contentId for custom races */
  raceContentRef?: string;
  subrace?: string;
  classes: CharacterClass[];
  /** Computed total character level (sum of class levels) */
  totalLevel: number;
  alignment: DndAlignment;
  size: SizeCategory;
  /** Deity (optional, common in D&D 3.5 / PF1) */
  deity?: string;
  /** D&D 3.5 / PF1 background description (free-text, not a mechanical template) */
  background?: string;

  // Ability scores & modifiers
  abilityScores: AbilityScores;
  /** Temporary ability score adjustments (e.g. from magic items, effects) */
  abilityScoreAdjustments?: Partial<AbilityScores>;

  // Combat stats
  maxHp: number;
  currentHp: number;
  nonLethalDamage: number;   // D&D 3.5 / PF1 non-lethal damage
  tempHp: number;
  armorClass: number;        // full AC
  touchAC: number;
  flatFootedAC: number;
  /** AC component breakdown (armor, shield, dex, natural, deflection, dodge, misc) */
  acBreakdown?: AcBreakdown;
  speed: number;             // base land speed in feet
  /** Non-land movement speeds: fly, swim, climb, burrow */
  additionalSpeeds?: AdditionalSpeeds;
  initiative: number;        // initiative modifier

  // Base Attack Bonus
  baseAttackBonus: number;

  // Saving throws
  fortitudeSave: number;
  reflexSave: number;
  willSave: number;

  // Grapple modifier (D&D 3.5 only — PF1 uses CMB/CMD instead, see below)
  /** D&D 3.5: grapple modifier = BAB + STR mod + size mod */
  grappleModifier?: number;

  // Combat Maneuver Bonus / Defense (PF1 only)
  /** PF1 only: CMB = BAB + STR mod + size mod */
  cmb?: number;
  /** PF1 only: CMD = 10 + BAB + STR mod + DEX mod + size mod */
  cmd?: number;

  // Damage Reduction and Special Defenses
  damageReduction?: string;   // e.g. "5/magic"
  spellResistance?: number;

  // Skills
  /**
   * All skills tracked per character.
   * D&D 3.5: cross-class skills cost 2 points/rank, max ranks = level + 3.
   * PF1: all skills cost 1 point/rank; class skills get +3 when ≥1 rank.
   */
  skills: SkillEntry[];

  // Feats
  /**
   * Feats — stored as references.
   * D&D 3.5: bonus feat at 1st level for humans, every 3 levels (1,3,6,9...) for all.
   * PF1: every odd level (1,3,5,7...).
   */
  feats: CharacterFeature[];

  // Spellcasting (optional)
  spells?: SpellEntry[];
  spellSlots?: SpellSlotTracker;
  /**
   * Per-caster spell slots for multi-caster characters (casterIndex 1 and 2).
   * Takes precedence over `spellSlots` when present — `spellSlots` is kept for
   * backward-compatible single-caster imports.
   */
  multiCasterSlots?: MultiCasterSlots;
  /**
   * Spell point pools (Power Points, Harbinger Spell Points, Bardic Music uses, etc.).
   * Any resource that is tracked as a pool rather than per-level slots.
   */
  spellPointPools?: SpellPointPool[];
  /**
   * Spells known per level for spontaneous casters (sorcerer, bard, oracle, etc.).
   * Prepared casters (wizard, cleric) leave this undefined.
   */
  spellsKnown?: SpellsKnownTracker;
  /** Relevant for sorcerers, bards, oracles, etc. — bonus spells from ability score */
  primaryCastingAbility?: keyof AbilityScores;
  spellSaveDC?: number;       // 10 + spell level + ability mod
  arcaneSpellFailure?: number; // percentage
  /**
   * PF1 concentration check total: caster level + casting ability mod + misc.
   * Used for casting defensively, in severe weather, etc.
   */
  concentrationBonus?: number;

  // Special abilities, class features, racial traits
  features: CharacterFeature[];
  /** PF1 racial traits (may replace standard traits) */
  racialTraits: CharacterFeature[];
  /** PF1 character traits (the 2 at-character-creation bonus traits) */
  characterTraits?: CharacterFeature[];
  specialAbilities: CharacterFeature[];
  spellLikeAbilities?: CharacterFeature[];  // from Roll20 repeating_spell-like (non-pool entries)

  // Equipment
  equipment: EquipmentItem[];
  /** In-world coin purse (platinum, gold, silver, copper) */
  coinPurse: { cp: number; sp: number; gp: number; pp: number };
  /** Campaign-scoped custom currency balances by CampaignCurrency.currencyId. */
  customCurrencyBalances?: Record<string, number>;
  totalWeight?: number;
  carryCapacity?: { light: number; medium: number; heavy: number };

  // Personality & backstory
  personality: string;
  ideals?: string;
  bonds?: string;
  flaws?: string;
  backstory: string;

  // Physical description (fields from the Pathfinder Official character sheet)
  /** Age — stored as a string to accommodate values like "Unknown" or "350 (appears 30)" */
  age?: string;
  /** Height string, e.g. "5'11\"" */
  height?: string;
  /** Weight string, e.g. "185 lbs" */
  weight?: string;
  eyes?: string;
  hair?: string;
  /** Place of origin or homeland */
  homeland?: string;
  gender?: string;

  notes: string;

  /** XP total (both systems track it; PF1 uses a fixed table, 3.5 uses the same) */
  xp?: number;
  /** XP required to reach the next level (informational) */
  xpNextLevel?: number;

  // NPC / Creature fields (populated by Roll20 NPC sheet import)
  /**
   * Creature type string as written on the NPC sheet, e.g. "magical beast (mythic)".
   * Not applicable to standard player characters.
   */
  npcType?: string;
  /**
   * Challenge Rating — stored as a string to preserve fractional values ("1/2", "1/3").
   * Convert to a number only for display/sorting; keep the original string for output.
   */
  challengeRating?: string;
  /** Pathfinder Mythic Adventures: mythic rank (1–10) for mythic NPCs and characters. */
  mythicRank?: number;

  avatarImageKey?: string;
  /** Pre-signed GET URL generated at read time; not stored in DynamoDB. */
  avatarImageUrl?: string;
  portraitImageKey?: string;
  /** Pre-signed GET URL generated at read time; not stored in DynamoDB. */
  portraitImageUrl?: string;
  createdAt: string;
  updatedAt: string;

  // ---------------------------------------------------------------------------
  // World / Campaign assignment (set by DM, read-only for players)
  // ---------------------------------------------------------------------------
  /** 'world' = assigned to a world but no specific campaign; 'campaign' = in a campaign */
  assignmentType?: 'unassigned' | 'world' | 'campaign';
  /** Present when assignmentType is 'world' or 'campaign'. */
  worldId?: string;
  /** Present when assignmentType is 'campaign'. */
  campaignId?: string;

  /**
   * When true, this character's player can distribute claimed loot for the
   * campaign they are assigned to.  Only DM/admin users may set this flag.
   * Automatically cleared when the character is unassigned from a campaign.
   */
  isLootMaster?: boolean;

  /**
   * Set when the character was imported from Roll20.
   * Written once at import time; not editable via PATCH /characters/{id}.
   */
  importMeta?: Roll20ImportMeta;

  /**
   * Set when the character was imported from D&D Beyond.
   * Updated on every re-import. Not editable via PATCH /characters/{id}.
   */
  dndbeyondImportMeta?: DnDBeyondImportMeta;

  // ---------------------------------------------------------------------------
  // D&D 5e — system-specific fields (absent/ignored for dnd35 / pathfinder1)
  // ---------------------------------------------------------------------------

  /**
   * D&D 5e Proficiency Bonus.
   * Levels 1–4: +2 | 5–8: +3 | 9–12: +4 | 13–16: +5 | 17–20: +6
   * Stored explicitly after import; can also be computed from totalLevel.
   */
  proficiencyBonus?: number;

  /**
   * D&D 5e saving throw proficiencies — array of ability keys.
   * e.g. ['str', 'con'] for a Fighter.
   */
  savingThrowProficiencies?: Array<keyof AbilityScores>;

  /** D&D 5e Inspiration — true when the character currently holds inspiration. */
  inspiration?: boolean;

  /**
   * D&D 5e Death Save state.
   * successes and failures each range from 0–3. Resets on stabilisation / long rest.
   */
  deathSaves?: { successes: number; failures: number };

  /**
   * D&D 5e Hit Dice tracking for short rest recovery.
   * total = sum of all class levels; remaining = unspent since last long rest.
   */
  hitDice?: { total: number; remaining: number; dieType: string };

  /**
   * D&D 5e mechanical Background name (e.g. "Acolyte", "Criminal", "Folk Hero").
   * Distinct from the free-text `background` field used for narrative backstory.
   */
  background5e?: string;

  /** D&D 5e Passive Perception (10 + Perception modifier). Stored after import. */
  passivePerception?: number;

  /** D&D 5e Passive Investigation (10 + Investigation modifier). Stored after import. */
  passiveInvestigation?: number;

  /** D&D 5e Spell Attack Bonus (Proficiency Bonus + casting ability modifier). */
  spellAttackBonus?: number;

  // ---------------------------------------------------------------------------
  // Owner display info (response-only — NOT stored in DynamoDB; injected by the
  // API at query time from the User table so callers always get a fresh name).
  // ---------------------------------------------------------------------------
  /** Immutable Discord handle of the character's owner. */
  ownerUsername?: string;
  /** User-chosen display name of the character's owner (may be absent). */
  ownerDisplayName?: string;
  /** Avatar URL of the character's owner (Discord CDN or custom upload). */
  ownerAvatarUrl?: string;
}

// ---------------------------------------------------------------------------
// Custom / Imported Game Content (races, classes, feats, spells, items, etc.)
// ---------------------------------------------------------------------------
export type GameContentType =
  | 'race' | 'class' | 'prestige_class' | 'feat' | 'spell'
  | 'skill' | 'equipment' | 'magic_item' | 'monster' | 'other';

export interface GameContent {
  contentId: string;
  /** The rule system this content belongs to (or 'custom' for homebrew) */
  ruleSystem: RuleSystem;
  type: GameContentType;
  name: string;
  description: string;
  /** Structured data varies by content type — stored as JSON blob */
  data: Record<string, unknown>;
  /** True if sourced from SRD/OGL (publicly reproducible) */
  isSrd: boolean;
  /** Who created this content (null = system/imported) */
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Worlds
// ---------------------------------------------------------------------------
export type WorldStatus = 'active' | 'archived';

export interface World {
  worldId: string;
  ownerUserId: string;
  name: string;
  description: string;
  /** Free-text genre/theme, e.g. "High Fantasy", "Forgotten Realms", "Homebrew" */
  setting: string;
  status: WorldStatus;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// World Members (players invited to a World by its DM)
// ---------------------------------------------------------------------------
export interface WorldMember {
  worldId: string;
  userId: string;
  /** Denormalized from User at add time */
  username: string;
  displayName?: string;
  /** Discord CDN URL or custom avatar URL, denormalized at add time */
  avatarUrl?: string;
  addedByUserId: string;
  joinedAt: string;
}

// ---------------------------------------------------------------------------
// Campaigns / Modules
// ---------------------------------------------------------------------------
export type CampaignStatus = 'active' | 'on_hiatus' | 'completed';

export interface Campaign {
  campaignId: string;
  worldId: string;
  /** Denormalized from World — always equals world.ownerUserId */
  ownerUserId: string;
  name: string;
  description: string;
  status: CampaignStatus;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  // ── Discord integration settings (DM-configured, optional) ──────────────────
  /**
   * Discord channel snowflake for auto-posting combat roll results.
   * Set by the DM in campaign settings. When present, announceRolls and
   * executeRoutine automatically post to this channel via the Discord bot.
   */
  combatChannelId?: string;
  /**
   * Discord channel snowflake for loot distribution messages.
   * Used by the lootchests Lambda when distributing loot to players.
   */
  discordLootChannelId?: string;
  /**
   * Discord category snowflake that this campaign's channels live under.
   * Used by the pin-sync system to route Discord pins to the correct board.
   */
  discordCategoryId?: string;
}

/** Campaign and character choices available from the global Combat shortcut. */
export interface CombatContextCampaign {
  campaignId: string;
  worldId: string;
  name: string;
  characters: Array<Pick<Character, 'characterId' | 'name' | 'ruleSystem' | 'worldId' | 'campaignId'>>;
}

export interface CombatContextResponse {
  campaigns: CombatContextCampaign[];
  unassignedCharacters: Array<Pick<Character, 'characterId' | 'name' | 'ruleSystem' | 'worldId' | 'campaignId'>>;
}

// ---------------------------------------------------------------------------
// Campaign Resources (PDFs, artwork, external links — visible to all players)
// ---------------------------------------------------------------------------
export type CampaignResourceType = 'pdf' | 'image' | 'link';

export interface CampaignResource {
  resourceId: string;
  campaignId: string;
  worldId: string;
  type: CampaignResourceType;
  name: string;
  description?: string;
  /** Present for type 'link' */
  url?: string;
  /** Present for type 'pdf' or 'image' — S3 object key */
  s3Key?: string;
  /** File size in bytes (for uploaded files) */
  fileSize?: number;
  /** MIME content type (for uploaded files) */
  contentType?: string;
  /** Who added this resource */
  addedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Campaign Marketplaces (campaign-scoped; distinct from the global shop)
// ---------------------------------------------------------------------------
export type MarketplaceType = 'general' | 'arms' | 'magic' | 'black_market' | 'specialty';

export interface CampaignMarketplace {
  marketplaceId: string;
  campaignId: string;
  /** Denormalized from Campaign */
  worldId: string;
  name: string;
  description: string;
  marketplaceType: MarketplaceType;
  isActive: boolean;
  /** When true, players may buy campaign custom currencies here at their conversion value. */
  allowCurrencyConversion?: boolean;
  /** Per-currency stock for conversions. -1 means unlimited for that currency. */
  currencyConversionStock?: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignCurrency {
  currencyId: string;
  campaignId: string;
  name: string;
  description: string;
  /** Optional conversion cost in gold pieces. Undefined means not directly purchasable. */
  conversionGp?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignMarketplaceItem {
  itemId: string;
  marketplaceId: string;
  name: string;
  description: string;
  /** Price in gold pieces (gp) */
  price: number;
  /** Optional extra campaign-scoped custom currency costs by currencyId. */
  customCurrencyCosts?: Record<string, number>;
  category: ItemCategory;
  rarity: ItemRarity;
  /** -1 = unlimited */
  stock: number;
  isActive: boolean;
  imageKey?: string;
  /** Optional hyperlink to an external reference (e.g. D&D Beyond, Roll20 compendium). */
  sourceUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// In-world coin currency
// Conversion: 1 pp = 10 gp = 100 sp = 1,000 cp
// ---------------------------------------------------------------------------
export type CoinDenomination = 'pp' | 'gp' | 'sp' | 'cp';

export const COIN_TO_CP: Record<CoinDenomination, number> = {
  cp: 1,
  sp: 10,
  gp: 100,
  pp: 1000,
} as const;

// ---------------------------------------------------------------------------
// Party Loot Chest  (exactly one per Campaign — created atomically with it)
// ---------------------------------------------------------------------------
export interface PartyLootChest {
  /** Same as parent Campaign's campaignId — the primary key. */
  campaignId: string;
  worldId: string;
  name: string;
  description: string;
  coinPp: number;
  coinGp: number;
  coinSp: number;
  coinCp: number;
  createdAt: string;
  updatedAt: string;
}

export interface LootItem {
  lootItemId: string;
  campaignId: string;
  name: string;
  description: string;
  quantity: number;
  estimatedValue: number;
  valueCurrency: CoinDenomination;
  /** Item category — used for display and carried over to EquipmentItem on distribute. */
  category?: ItemCategory;
  /** Item rarity — used for display and carried over to EquipmentItem on distribute. */
  rarity?: ItemRarity;
  /** Weight in pounds — carried over to EquipmentItem on distribute. */
  weight?: number;
  /** Optional hyperlink to an external reference (e.g. Archives of Nethys, D&D Beyond). */
  sourceUrl?: string;
  notes?: string;
  acquiredAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Loot Claims  (players request specific items from the chest)
// ---------------------------------------------------------------------------

/** A single player's request to receive an item from the party loot chest. */
export interface LootClaim {
  claimId: string;
  campaignId: string;
  /** The loot item being claimed. */
  lootItemId: string;
  /** The user who submitted the claim. */
  claimedByUserId: string;
  /** Display name of the claiming user (denormalized at claim time). */
  claimedByDisplayName: string;
  /** The character that will receive the item if the claim is granted. */
  characterId: string;
  /** Character name (denormalized at claim time for display). */
  characterName: string;
  /** How many of this item the player is requesting. */
  quantity: number;
  claimedAt: string;
}

/** Result of a d20 roll for one claim in a contested distribution. */
export interface LootClaimRollResult {
  claimId: string;
  characterId: string;
  characterName: string;
  userId: string;
  userDisplayName: string;
  quantityRequested: number;
  /** Allocated quantity after contest resolution (0 if lost). */
  quantityAllocated: number;
  roll: number;
  won: boolean;
}

/** Summary of how one item was resolved during a distribute-claimed action. */
export interface LootClaimDistribution {
  lootItemId: string;
  itemName: string;
  availableQuantity: number;
  totalClaimed: number;
  contested: boolean;
  rolls: LootClaimRollResult[];
}

/**
 * Record of a single DM distribute-claimed action.
 * Stored at PK=`CAMPAIGN#<id>`, SK=`LOOTCLAIMLOG#<processedAt>#<claimLogId>`.
 */
export interface LootClaimLog {
  claimLogId: string;
  campaignId: string;
  processedByUserId: string;
  distributions: LootClaimDistribution[];
  processedAt: string;
}

// ---------------------------------------------------------------------------
// Loot Import Log  (one record per DM bulk-import action)
// ---------------------------------------------------------------------------

/**
 * Outcome of a single parsed line from a bulk loot import.
 * `action`:
 *   - `'created'`  — a brand-new LootItem was added to the chest.
 *   - `'merged'`   — an existing LootItem with a similar name had its quantity increased.
 *   - `'coin'`     — the line was interpreted as loose currency and added to the coin balance.
 *   - `'skipped'`  — the line was blank, a comment, or could not be parsed.
 */
export interface LootImportResult {
  rawLine: string;
  action: 'created' | 'merged' | 'coin' | 'skipped';
  /** Resolved item name (absent for coin / skipped actions). */
  name?: string;
  /** Quantity applied (absent for coin / skipped actions). */
  quantity?: number;
  /** Estimated value applied (absent when not parseable or when action is coin/skipped). */
  estimatedValue?: number;
  valueCurrency?: CoinDenomination;
  /** lootItemId of the created or updated LootItem (absent for coin / skipped). */
  lootItemId?: string;
  /** When `action === 'coin'`: amount added to the chest coin balance. */
  coinAmount?: number;
  coinDenomination?: CoinDenomination;
}

/**
 * Record of a single DM bulk-import action.
 * Stored in DynamoDB under PK=`CAMPAIGN#<id>`, SK=`LOOTIMPORT#<importedAt>#<lootImportId>`.
 * Allows the DM to audit what was imported, from which encounter, and when.
 */
export interface LootImportLog {
  lootImportId: string;
  campaignId: string;
  /** Human-readable label provided by the DM, e.g. "Throne Room", "Encounter 3". */
  encounterName: string;
  importedByUserId: string;
  /** Original text pasted or uploaded by the DM — stored verbatim for auditability. */
  rawText: string;
  /** Per-line results from the parser + DB operations. */
  results: LootImportResult[];
  /** Total new LootItems created. */
  itemsCreated: number;
  /** Total existing LootItems whose quantity was increased. */
  itemsMerged: number;
  /**
   * Coin amounts added to the chest's balance during this import.
   * Only denominations that received a non-zero delta are present.
   */
  coinsAdded: Partial<Record<CoinDenomination, number>>;
  importedAt: string;
}

export type ItemCategory = 'weapon' | 'armor' | 'potion' | 'component' | 'mount' | 'misc';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'very_rare' | 'legendary';

export interface InventoryEntry {
  itemId: string;
  userId: string;
  /** Character that purchased / owns this item. */
  characterId?: string;
  /** Marketplace the item was purchased from. */
  marketplaceId?: string;
  /** Campaign the marketplace belongs to. */
  campaignId?: string;
  quantity: number;
  acquiredAt: string;
}

export type MarketplaceTransactionType = 'ITEM_PURCHASE' | 'CURRENCY_CONVERSION' | 'LEDGER_ADJUSTMENT';

export interface MarketplaceTransaction {
  transactionId: string;
  campaignId: string;
  marketplaceId?: string;
  itemId?: string;
  currencyId?: string;
  characterId: string;
  characterName: string;
  userId: string;
  type: MarketplaceTransactionType;
  quantity: number;
  gpDelta: number;
  customCurrencyDeltas: Record<string, number>;
  description: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  updatedByUserId?: string;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export interface PaginatedResponse<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
  total?: number;
}

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

// ---------------------------------------------------------------------------
// Warbach Feedback System
// ---------------------------------------------------------------------------
export type FeedbackType   = 'bug' | 'feature' | 'general';
export type FeedbackStatus = 'open' | 'in_progress' | 'resolved' | 'wontfix';

export interface FeedbackReport {
  feedbackId: string;
  submittedByUserId: string;
  submittedByUsername: string;
  /** User-chosen display name (denormalized at submit time). */
  submittedByDisplayName?: string;
  /** Avatar URL (denormalized at submit time). */
  submittedByAvatarUrl?: string;
  type: FeedbackType;
  /** Free-text description written by the user. */
  description: string;
  /** Full page URL at the time of submission. */
  pageUrl: string;
  /** document.title at time of submission. */
  pageTitle: string;
  /** navigator.userAgent */
  userAgent: string;
  /** "WxH" browser viewport dimensions. */
  viewport: string;
  /** Any extra structured context (route params, selected entity IDs, etc.). */
  context?: Record<string, unknown>;
  status: FeedbackStatus;
  /** Developer / admin review notes. */
  adminNotes?: string;
  submittedAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Library of Nix
// ---------------------------------------------------------------------------
export type LibraryDocCategory =
  | 'rulebook'       // Core rulebooks / expansion books
  | 'module'         // Adventure modules
  | 'supplement'     // Supplemental PDFs / supplements
  | 'media'          // Images, video, audio
  | 'documentation'  // Site guides / how-to docs
  | 'other';

/**
 * Who can see a library document.
 *   owner    — uploader only (private)
 *   world    — all members of a specific World
 *   campaign — all members of a specific Campaign's parent World
 *   all      — any authenticated user
 *
 * Admins (and moderators) always see every document regardless of this setting.
 * Legacy documents that pre-date this field are treated as 'all'.
 */
export type LibraryDocVisibility = 'owner' | 'world' | 'campaign' | 'campaign_dm' | 'all';

export interface LibraryDocument {
  docId: string;
  title: string;
  description?: string;
  category: LibraryDocCategory;
  /** Original file name as uploaded. */
  fileName: string;
  /** MIME type (e.g. "application/pdf"). */
  fileType: string;
  /** File size in bytes. */
  fileSize: number;
  /** S3 object key within LIBRARY_BUCKET. */
  s3Key: string;
  uploadedBy: string;         // userId
  uploaderUsername?: string;
  uploaderDisplayName?: string;
  uploaderAvatarUrl?: string;
  tags?: string[];
  /**
   * Visibility setting. Defaults to 'owner' for newly uploaded documents.
   * Legacy documents without this attribute are treated as 'all' at read time.
   */
  visibility: LibraryDocVisibility;
  /**
   * For 'world' visibility: the worldId.
   * For 'campaign' visibility: the campaignId.
   */
  visibilityTargetId?: string;
  /** Denormalized display name of the target world or campaign. */
  visibilityTargetName?: string;
  /**
   * For 'campaign' visibility only — the worldId of the campaign.
   * Stored to enable fast world-membership checks without an extra GSI lookup.
   */
  visibilityWorldId?: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Donations — Stripe Checkout-based one-time site-support payments
// ---------------------------------------------------------------------------
export type DonationStatus = 'pending' | 'completed' | 'refunded';

export interface Donation {
  donationId:             string;
  /** Stripe Checkout Session ID — used as the DynamoDB SK for idempotency. */
  stripeSessionId:        string;
  /** Stripe PaymentIntent ID — populated once the session is completed. */
  stripePaymentIntentId?: string;
  /** Cognito userId of the donor — from session metadata. */
  userId?:                string;
  /** Cognito username of the donor — from session metadata. */
  username?:              string;
  /** Optional thank-you note left by the donor. */
  message?:               string;
  /** Donation amount in the smallest currency unit (e.g. cents for USD). */
  amountCents:            number;
  /** ISO 4217 currency code, lower-case (e.g. "usd"). */
  currency:               string;
  status:                 DonationStatus;
  createdAt:              string;
  completedAt?:           string;
}

// ---------------------------------------------------------------------------
// Combat Automation — Phase 2+: Attack Routines & Roll Engine Types
// ---------------------------------------------------------------------------

/**
 * A single component of a structured attack modifier or damage modifier.
 * Multiple components are summed together to produce the total modifier.
 * Replaces the single-value attackMod / attackModSource on AttackNode and
 * the embedded flat modifier in DamageNode.diceExpr for new routines.
 */
export interface ModifierComponent {
  id:          string;
  type:        'stat_ref' | 'manual';
  /** Stat key resolved against the character sheet at roll time (stat_ref only).
   *  e.g. 'bab', 'str_mod', 'bab_2nd' */
  statKey?:    string;
  /** Fixed integer bonus or penalty (manual only, signed). */
  value?:      number;
  /** Human-readable label for a manual modifier, e.g. "Weapon Focus", "Rapid Shot". */
  descriptor?: string;
}

/** One resolved modifier component — stat key replaced with its live numeric value. */
export interface ResolvedModComponent {
  label: string;   // e.g. "BAB Max", "STR Modifier", "Weapon Focus"
  value: number;   // resolved integer value
}

/**
 * One damage row within an attack.
 * The diceExpr field contains dice notation only (e.g. "2d6").
 * Flat modifiers are supplied via modComponents.
 */
/** Creature/weapon size category — used for size-based damage scaling. */
export type WeaponSize =
  | 'fine' | 'diminutive' | 'tiny' | 'small' | 'medium'
  | 'large' | 'huge' | 'gargantuan' | 'colossal';

/**
 * Category for special (non-hit-point) damage — ability score damage and
 * negative levels.  When set on a DamageNode, the roll engine excludes the
 * row from totalDamage and accumulates it separately in
 * CompletedRollResult.specialDamageTotals instead.
 */
export type SpecialDamageType =
  | 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha'
  | 'negative_level';

/** Human-readable labels for each SpecialDamageType. */
export const SPECIAL_DAMAGE_LABELS: Record<SpecialDamageType, string> = {
  str:            'Str Damage',
  dex:            'Dex Damage',
  con:            'Con Damage',
  int:            'Int Damage',
  wis:            'Wis Damage',
  cha:            'Cha Damage',
  negative_level: 'Negative Level',
};

export interface DamageNode {
  id:       string;
  /** Dice expression in standard notation, e.g. "2d6+15", "1d8", "3d6+33". */
  diceExpr: string;
  /** Combined type / notes field, e.g. "Slashing", "Fire", "Cold (Frost sword)". */
  notes:    string;
  /**
   * Critical hit multiplier for this damage row.
   *   1  = not multiplied on a crit (e.g. sneak attack dice, energy rider)
   *   2+ = multiplied by this value on a crit (D&D 3.5 / PF1)
   * In D&D 5e only the dice are doubled regardless of this value,
   * but critMult = 1 still suppresses all multiplication.
   */
  critMult: number;
  /**
   * Phase 3+: structured modifier components (stat refs + manual bonuses with
   * descriptors) that are added to the rolled dice.  When present the flat
   * modifier formerly embedded in diceExpr should be omitted.
   */
  modComponents?: ModifierComponent[];
  /**
   * Optional weapon/creature size for this damage row.
   * When set, the roll engine applies size-based dice scaling (PF1/3.5 rules)
   * using the size step delta supplied at roll time.
   */
  weaponSize?: WeaponSize;
  /**
   * When set, this row represents non-HP damage (ability score drain or
   * negative levels).  The roll engine excludes it from totalDamage and
   * accumulates the total in CompletedRollResult.specialDamageTotals instead.
   * Absent = normal HP damage (backward-compatible with all existing routines).
   */
  specialType?: SpecialDamageType;
}

/** Attack category used by combat modifiers targeting melee/ranged/spell rolls. */
export type AttackKind = 'melee' | 'ranged' | 'spell';

/** D&D 3.5 weapon properties that add confirmed-critical severing effects. */
export type WeaponSpecialProperty = 'vorpal' | 'sharpness';

export interface WeaponSpecialEffectResult {
  property: WeaponSpecialProperty;
  /** Natural d20 that qualified for the effect. */
  naturalRoll: number;
  /** Rules-facing effect summary. */
  effect: string;
  /** Random limb result for Sharpness. */
  limb?: string;
  /** Target-size/material rule reminder for Sharpness. */
  eligibility?: string;
}

/**
 * One attack step within a routine.
 * Phase 2: the only node type.  Phase 3 will extend with ConditionalNode,
 * BonusAttackNode, and VariableNode.
 */
export interface AttackNode {
  type:       'attack';
  id:         string;
  label:      string;            // e.g. "Primary Strike", "Off-hand"
  /**
   * Optional category for this attack. When set, campaign modifiers targeting
   * attack.melee / attack.ranged / attack.spell apply only to matching attacks.
   * Absent = legacy/general attack; only modifiers targeting "attack" apply.
   */
  attackKind?: AttackKind;
  attackMod:  number;            // total attack modifier (e.g. 20 for BAB+20)
  /**
   * Phase 3 — live stat reference source for the attack modifier.
   * When present, the roll engine resolves this against the live character
   * record at roll time rather than using the static `attackMod` number.
   * { type: 'manual', value: 20 } or { type: 'stat_ref', key: 'bab' }
   * Stored as a plain JSON object; absent on Phase 2 routines (backwards-compat).
   */
  attackModSource?: { type: 'manual'; value: number } | { type: 'stat_ref'; key: string };
  /**
   * Phase 3+: structured modifier components — array of stat refs and manual bonuses
   * with descriptors that sum to the total attack bonus.
   * Replaces attackMod + attackModSource for new routines.
   * Absent on Phase 2 routines (backwards-compat: use attackMod instead).
   */
  attackComponents?: ModifierComponent[];
  vsTarget:   string;            // 'AC' | 'Touch AC' | 'Flat-Footed AC' | 'CMD'
  critRange:  [number, number];  // [min, 20], e.g. [18, 20] or [20, 20]
  /**
   * When true this attack cannot score a critical hit regardless of the die roll.
   * Use for combat maneuver checks (CMB vs CMD), skill-based attacks, and any
   * other roll where the rules explicitly state no critical is possible.
   */
  noCrit?:    boolean;
  /**
   * D&D 5e only — critical hit damage style.
   * 'standard'  (default) — roll the dice twice (i.e. double the rolled dice sum), add modifier once.
   * 'perfect'   — maximize the base dice, add a rolled critical dice set, add modifier once.
   *               Models homebrew/feature interactions where crits use max base dice
   *               (e.g. Brutal Critical variants, Perfect Roll house rule).
   * Absent / undefined is treated as 'standard' for backward compatibility.
   */
  critStyle?: 'standard' | 'perfect';
  /**
   * D&D 3.5 weapon properties applied to this attack. These are attack-level
   * riders, not damage rows: Vorpal triggers only on a natural 20 confirmed
   * as a critical hit; Sharpness triggers on natural 18+ confirmed as a
   * critical hit and severs a non-head limb at random.
   */
  weaponProperties?: WeaponSpecialProperty[];
  /** On-hit damage rows — applied on every hit.  Each row carries its own critMult:
   *   1  = not multiplied on a crit (e.g. always-on energy rider)
   *   2+ = multiplied on a crit per system rules
   */
  damage:        DamageNode[];
  /**
   * On-crit-ONLY bonus damage — rolled and added ONLY on a confirmed critical hit.
   * These are never multiplied; they represent the extra burst dice that the rules
   * grant specifically for criticals (e.g. flaming burst sword's +1d10 fire on crit).
   */
  onCritExtra:   DamageNode[];
}

/**
 * Condition expression for a ConditionalBonusDamageNode.
 *   subject 'hits'      — total confirmed hits across all primary attacks
 *   subject 'crit_hits' — total critical hits among confirmed hits
 */
export interface ConditionalBonusDamageCondition {
  subject: 'hits' | 'crit_hits';
  op:      'gte' | 'lte' | 'eq';
  value:   number;
}

/**
 * Bonus damage applied after all primary attacks resolve, but only when the
 * condition is met.  Pre-rolled in Phase A; gated in Phase C.
 * Examples: Sneak Attack (hits ≥ 1), Flurry Bonus (hits ≥ 2), Cleave (hits ≥ 1).
 */
export interface ConditionalBonusDamageNode {
  type:      'conditional_damage';
  id:        string;
  condition: ConditionalBonusDamageCondition;
  damage:    DamageNode[];
  label?:    string;   // e.g. "Sneak Attack (2+ hits)", "Flurry Bonus"
}

/** Pre-rolled conditional bonus damage result — `triggered` set in Phase C. */
export interface ConditionalDamageRollResult {
  nodeId:    string;
  label?:    string;
  condition: ConditionalBonusDamageCondition;
  /** True if the condition was met during Phase C resolution. */
  triggered: boolean;
  damage:    DamageRollResult[];
  total:     number;   // 0 when not triggered
}

/**
 * Condition expression for a BonusAttackNode (Phase 5).
 *   subject 'hits'      — total confirmed hits from primary attacks
 *   subject 'crit_hits' — total critical hits among confirmed hits
 * Examples: Two-Weapon Fighting triggers when hits ≥ 1, On Crit triggers when crit_hits ≥ 1
 */
export interface BonusAttackCondition {
  subject: 'hits' | 'crit_hits';
  op:      'gte' | 'lte' | 'eq';
  value:   number;
}

/**
 * Conditional full attack — a secondary attack triggered when condition met.
 * Phase 5: Cleave, Two-Weapon Fighting bonus attacks, etc.
 * Not multiplied by Phase B hit selection; rolls as a complete independent AttackNode
 * when condition evaluates true in Phase C.
 * Examples: Cleave (bonus attack if primary hit ≥ 1), Flurry of Blows (extra attacks).
 */
export interface BonusAttackNode {
  type:      'bonus_attack';
  id:        string;
  condition: BonusAttackCondition;
  attack:    AttackNode;    // Full attack definition; rolled in Phase A
  label?:    string;        // e.g. "Cleave", "Flurry Extra"
}

/** Result of a bonus attack evaluation — attack results included only if triggered. */
export interface BonusAttackRollResult {
  nodeId:    string;
  label?:    string;
  condition: BonusAttackCondition;
  /**
   * Original node definition, carried through from Phase A so Phase C can
   * roll additional attacks for crit_hits chains without the routine definition.
   */
  nodeDefinition?: BonusAttackNode;
  /** True if the condition was met during Phase C resolution. */
  triggered: boolean;
  attackResults: AttackRollResult[]; // Empty if not triggered
}

/**
 * Union of all routine step node types.
 * Phase 5 adds BonusAttackNode.
 */
export type RoutineNode = AttackNode | ConditionalBonusDamageNode | BonusAttackNode;

/** Export targets supported by the execute endpoint. */
export type ExportTarget = 'discord' | 'clipboard';

/** A named attack sequence owned by a character. */
export interface AttackRoutine {
  routineId:    string;
  characterId:  string;
  userId:       string;
  name:         string;
  gameSystem:   RuleSystem;
  /** Ordered list of attack nodes.  Phase 2 supports AttackNode only. */
  definition:   RoutineNode[];
  exportTargets: ExportTarget[];
  createdAt:    string;
  updatedAt:    string;
}

// ---------------------------------------------------------------------------
// Roll Engine Result Types (client-side Phase A + C)
// ---------------------------------------------------------------------------

export interface DamageRollResult {
  nodeId:   string;
  /** Original dice expression for display, e.g. "2d6+15". */
  diceExpr: string;
  rolls:    number[];  // individual die results (empty for flat-modifier-only rows)
  modifier: number;   // flat modifier (includes campaign damage bonus)
  total:    number;   // sum(rolls) + modifier
  notes:    string;   // type / description, e.g. "Slashing"
  critMult: number;   // carried from DamageNode for Phase C crit calculation
  /** Carried from DamageNode — absent for normal HP damage rows. */
  specialType?: SpecialDamageType;
  /** Per-component breakdown used by formatters to show full calculation. */
  resolvedModComponents?: ResolvedModComponent[];
}

export interface AttackRollResult {
  nodeId:    string;
  label:     string;
  d20:       number;    // raw d20 value
  attackMod: number;    // effective modifier (includes campaign modifier bonuses)
  total:     number;    // d20 + attackMod
  vsTarget:  string;
  /** Per-component breakdown used by formatters to show full calculation. */
  resolvedAttackComponents?: ResolvedModComponent[];
  isThreat:     boolean;    // d20 fell in crit range
  isCrit:       boolean;    // Phase 2: auto-confirmed; Phase 3 adds confirmation roll
  rollMode:     RollMode;   // roll mode active for this attack
  /** Second d20 when advantage/disadvantage was active; absent in normal mode. */
  advantageDie?: number;
  damage:       DamageRollResult[];  // on-hit rows; each carries its own critMult
  /** Pre-rolled; added to the total only on a confirmed critical hit. */
  onCritExtraDamage: DamageRollResult[];
  /** Carried from AttackNode.critStyle — used by Phase C and formatters. */
  critStyle?: 'standard' | 'perfect';
  /** Carried from AttackNode.weaponProperties for Phase C effect resolution. */
  weaponProperties?: WeaponSpecialProperty[];
  /** Confirmed special weapon-property effects for this attack. */
  specialEffects?: WeaponSpecialEffectResult[];
}

/** Roll mode for D&D 5e advantage / disadvantage mechanic. */
export type RollMode = 'normal' | 'advantage' | 'disadvantage';

export interface PendingRollResult {
  routineId:          string;
  routineName:        string;
  /**
   * Character name carried from the call site so the Phase A VTT dispatch
   * card can show who is attacking (e.g. "Piper") in the card header.
   * Optional for backward-compatibility with records written before this field.
   */
  characterName?:     string;
  /**
   * Carried from AttackRoutine.gameSystem so Phase C can apply the
   * correct critical-hit multiplication rule without needing to
   * re-fetch the routine.
   */
  gameSystem:         RuleSystem;
  rollMode:           RollMode;  // normal / advantage / disadvantage
  attackResults:      AttackRollResult[];
  /** Pre-rolled conditional bonus damage nodes; triggered state set in Phase C. */
  conditionalDamageResults: ConditionalDamageRollResult[];
  /** Pre-rolled bonus attacks (Phase 5); triggered flag set in Phase C. */
  bonusAttackResults?: BonusAttackRollResult[];
  appliedModifierIds:   string[];   // campaign modifier IDs that were applied
  /**
   * Human-readable names for each applied modifier, in the same order as
   * appliedModifierIds.  Used by VTT dispatch formatters to show a
   * "Modifiers: Bless · Inspire Greatness" footer on the chat card.
   * Optional for backward-compatibility with records written before this field.
   */
  appliedModifierNames?: string[];
  /**
   * Darkfeld site theme ID active when the roll was executed (e.g. 'darkfeld',
   * 'arcane', 'twilight', 'ember', 'void').  Embedded in every Roll20 dispatch
   * payload so the DarkfeldExport Mod script renders the card in a matching
   * palette.  Falls back to 'default' (= 'darkfeld' palette) when absent.
   */
  r20Theme?: string;
  rolledAt:           number;    // Date.now()
}

export interface ResolvedAttackResult extends AttackRollResult {
  isHit:        boolean;
  attackDamage: number;  // total damage dealt (0 if miss)
}

export interface CompletedRollResult {
  routineId:          string;
  routineName:        string;
  characterName?:     string;
  /** Carried from the routine so formatters can apply the correct crit display rule. */
  gameSystem:         RuleSystem;
  rollMode:           RollMode;
  resolvedAttacks:    ResolvedAttackResult[];
  totalDamage:        number;
  hits:               number;
  totalAttacks:       number;
  summary:            string;
  appliedModifierIds:   string[];
  /**
   * Human-readable names for each applied modifier, in the same order as
   * appliedModifierIds.  Populated by the client at roll time (the modifier
   * objects are already in memory) and passed through Phase A → Phase C.
   * Used by VTT dispatch formatters for the "Modifiers:" footer on chat cards.
   * Optional for backward-compatibility with records written before this field.
   */
  appliedModifierNames?: string[];
  /**
   * Darkfeld site theme ID active when the roll was executed (e.g. 'darkfeld',
   * 'arcane', 'twilight', 'ember', 'void').  Embedded in every Roll20 dispatch
   * payload so the DarkfeldExport Mod script renders the card in a matching
   * palette.  Falls back to 'default' (= 'darkfeld' palette) when absent.
   */
  r20Theme?: string;
  completedAt:        number;   // Date.now()
  /** Evaluated conditional bonus damage — includes triggered=false entries for UI display. */
  conditionalDamageResults: ConditionalDamageRollResult[];
  /** Evaluated bonus attacks (Phase 5) — includes triggered=false entries for UI display. */
  bonusAttackResults?: BonusAttackRollResult[];
  /**
   * Aggregate totals for special (non-HP) damage types across all confirmed hits,
   * conditional damage, and bonus attacks.  Only present when at least one damage
   * row has specialType set.  e.g. { cha: 2, negative_level: 1 }
   */
  specialDamageTotals?: Partial<Record<SpecialDamageType, number>>;
}

/**
 * A named attack routine stored in the world-level shared library.
 * Characters can browse and import from this library; imports create an
 * independent copy that the player can modify freely.
 * Stored at PK=WORLD#{worldId}, SK=LIBROUTINE#{routineId}.
 */
export interface LibraryRoutine {
  routineId:    string;
  worldId:      string;
  name:         string;
  description?: string;
  gameSystem:   RuleSystem;
  definition:   RoutineNode[];
  tags?:        string[];
  createdBy:    string;        // userId of contributor
  createdByName?: string;      // display name at submit time
  createdAt:    string;
  updatedAt:    string;
}

// ---------------------------------------------------------------------------
// Combat Automation — Phase 1: Bonus Types & Campaign Combat Modifiers
// ---------------------------------------------------------------------------

/**
 * One mechanical effect within a CombatModifier.
 * e.g. "+8 Enhancement bonus to Strength"
 */
export interface ModifierEffect {
  effectId:        string;
  /**
   * References a built-in typeId from BUILT_IN_BONUS_TYPES, or a custom typeId.
   * Not required when rollModifier is set.
   */
  bonusTypeId?:    string;
  /**
   * Signed integer — positive = bonus, negative = penalty.
   * Not applicable when rollModifier is set.
   */
  value?:          number;
  /**
   * Normalised attribute key, e.g. 'str', 'ac', 'attack.melee', 'save.fort'.
   * Free-form string so custom attributes are always supported.
   */
  targetAttribute: string;
  /** Human-readable label shown in the UI, e.g. "Strength", "Armor Class". */
  targetLabel:     string;
  /** Optional free-text condition note, e.g. "when flanking". */
  condition?:      string;
  /**
   * When set, this effect grants advantage or disadvantage on d20 rolls
   * matching targetAttribute. bonusTypeId and value are not used.
   * Advantage + disadvantage from any sources cancel each other out.
   */
  rollModifier?:   'advantage' | 'disadvantage';
}

/**
 * A named buff or debuff shared across a campaign.
 * Stored at PK=CAMPAIGN#{campaignId}, SK=MODIFIER#{modifierId}.
 */
export interface CombatModifier {
  modifierId:      string;
  campaignId:      string;
  worldId:         string;
  /** Display name, e.g. "Piper's Inspire Greatness". */
  name:            string;
  sourceType:      'character' | 'npc' | 'item' | 'spell' | 'ability';
  /** characterId when sourceType is 'character'. */
  sourceId?:       string;
  /** Human-readable source label, e.g. "Piper (Bard 16)". */
  sourceName:      string;
  effects:         ModifierEffect[];
  /** When true, this modifier still applies in Antimagic / Dead Magic areas. */
  worksInAntimagic?: boolean;
  /**
   * When true the modifier is on for all characters by default.
   * Individual characters may override this via a ModifierState record.
   */
  defaultEnabled:  boolean;
  /** DM master on/off switch — false overrides all character states. */
  isActive:        boolean;
  /** null = applies to all game systems. */
  gameSystem?:     import('./index.js').RuleSystem | null;
  createdBy:       string;
  createdAt:       string;
  updatedAt:       string;
  /**
   * 'world'           — created by the campaign DM or admin; applies party-wide.
   * 'character'       — created by a player for their own character's abilities;
   *                     uses per-character toggles to specify which party members are affected
   *                     (e.g. "I cast Haste on Aragorn and Legolas").
   * 'self-only'       — personal ability modifier (e.g. Power Attack); only the owner sees it.
   *                     Other characters cannot toggle it, and it never appears on their lists.
   * Absent on pre-ownership records — treat as 'world' for backward compat.
   */
  ownerType?:        'character' | 'world' | 'self-only';
  /** ID of the character this modifier was created for (character-type or self-only only). */
  ownerCharacterId?: string;
  /** Display name of the owning character, or 'World' for world-type. */
  ownerCharacterName?: string;
}

/**
 * Response shape for GET /campaigns/{id}/combat/modifiers.
 * characterStates maps characterId → enabled override.
 * Only characters with a non-default state have an entry here.
 */
export interface CombatModifierWithStates extends CombatModifier {
  characterStates: Record<string, boolean>;
}

/**
 * Per-character enable/disable override for one modifier.
 * Only written when the override differs from CombatModifier.defaultEnabled.
 * Stored at PK=CAMPAIGN#{campaignId}, SK=MODIFIER_STATE#{modifierId}#{characterId}.
 */
export interface ModifierState {
  modifierId:   string;
  characterId:  string;
  campaignId:   string;
  enabled:      boolean;
  updatedAt:    string;
}

/**
 * A campaign-custom bonus type (supplements the built-in static list).
 * Stored at PK=CAMPAIGN#{campaignId}, SK=BONUSTYPE#{typeId}.
 */
export interface CustomBonusType {
  typeId:        string;
  campaignId:    string;
  name:          string;
  stackingRule:  'no_stack_highest' | 'stacks';
  notes?:        string;
  createdBy:     string;
  createdAt:     string;
}

/**
 * A roll result pending delivery to the browser plugin.
 * Written by announce-rolls (Phase A) and execute (Phase C); polled and acked
 * by the plugin. Stored at PK=USER#{userId}, SK=PENDINGROLL#{rollId} with
 * a 5-minute TTL.
 *
 * Phase 4: Browser Plugin.
 * Phase 5: Two-phase dispatch (attack_rolls then damage_results).
 */
export interface PendingRoll {
  rollId:            string;
  userId:            string;
  /**
   * 'attack_rolls'   — Phase A result dispatched immediately after rolling.
   *                    Contains all attack totals so VTT shows to-hit results.
   * 'damage_results' — Phase C result dispatched after hit selection.
   *                    Contains damage for confirmed hits only + grand total.
   *
   * Legacy records (written before Phase 5) have no type field; treat as
   * 'damage_results' for backward compatibility.
   */
  type?:             'attack_rolls' | 'damage_results';
  /**
   * Phase A payload (type = 'attack_rolls'): the PendingRollResult from
   * rollPhaseA — attacks, d20 values, totals vs target.
   */
  pendingResult?:    PendingRollResult;
  /**
   * Phase C payload (type = 'damage_results', or legacy records without type):
   * the CompletedRollResult from rollPhaseC — damage for confirmed hits.
   */
  completedResult?:  CompletedRollResult;
  createdAt:         string;
  /** TTL in epoch seconds (set by DynamoDB auto-expiry). */
  ttl:               number;
  /** When true, the plugin has acked delivery. */
  acknowledged?:     boolean;
  acknowledgedAt?:   string;
}

/**
 * A historical record of one completed combat roll.
 * Stored at PK=CHARACTER#{characterId}, SK=ROLLLOG#{timestamp}#{logId}.
 * Timestamp uses ISO-8601 format for natural sort ordering (newest first with reverse scan).
 */
export interface RollLog {
  logId:             string;
  characterId:       string;
  userId:            string;
  routineId:         string;
  routineName:       string;
  /** Snapshot of the CompletedRollResult at execution time. */
  result:            CompletedRollResult;
  createdAt:         string;
}

// ---------------------------------------------------------------------------
// JWT claims injected by Cognito Pre-Token-Generation trigger
export interface CognitoTokenClaims {
  sub: string;              // Cognito user ID
  'cognito:username': string;
  /** Cognito user groups: 'users' | 'dms' | 'moderators' | 'admins' */
  'cognito:groups'?: string[];
  'custom:discord_id'?: string;
  /** Injected by preTokenGeneration trigger — matches PK written by postConfirmation */
  'custom:user_id'?: string;
  /** Mapped from Cognito group to UserRole by pre-token-generation trigger */
  'custom:role'?: UserRole;
  'custom:status'?: string;
  /** Discord username — injected by preTokenGeneration onto the access token so
   *  API Gateway's JWT authorizer forwards it (cognito:username is not forwarded) */
  'custom:username'?: string;
  /** User's chosen display name — injected by preTokenGeneration onto the access token */
  'custom:display_name'?: string;
  email?: string;
}
